













































































































































import { Component, Vue, Prop, Ref, Watch } from "vue-property-decorator";
import { getImageUrl, getSharedImageUrl } from "@/data/repository/FirebaseImageRepository";

@Component
export default class LogoPreviewComponent extends Vue {
  @Prop({ default: null })
  logoFile!: string | null;

  @Prop({ default: null })
  newLogoFile!: File | null;

  @Prop({ default: 100 })
  scaleFactor!: number;

  @Prop({ default: false })
  readonly!: boolean;

  @Prop({ default: false })
  isMemberClient!: boolean;

  @Prop({ default: false })
  compactMode!: boolean;

  @Ref("upload")
  uploadElement!: HTMLInputElement;

  isDragging = false;
  showExpandDialog = false;
  resolvedLogoUrl: string | null = null;
  localScale = 100;

  @Watch("scaleFactor", { immediate: true })
  onScaleFactorChanged(val: number) {
    this.localScale = val;
  }

  @Watch("logoFile", { immediate: true })
  async onLogoFileChanged(path: string | null) {
    if (!path) {
      this.resolvedLogoUrl = null;
      return;
    }

    const primaryResolver = this.isMemberClient
      ? getSharedImageUrl
      : getImageUrl;
    const fallbackResolver = this.isMemberClient
      ? getImageUrl
      : getSharedImageUrl;

    try {
      const url = await primaryResolver(path);
      this.resolvedLogoUrl = url;
    } catch {
      try {
        const fallbackUrl = await fallbackResolver(path);
        this.resolvedLogoUrl = fallbackUrl;
      } catch {
        this.resolvedLogoUrl = null;
      }
    }
  }

  get hasLogo() {
    return !!this.newLogoFile || !!this.logoFile;
  }

  get previewUrl() {
    if (!this.newLogoFile) return null;
    return URL.createObjectURL(this.newLogoFile);
  }

  /** Logo max-width as percentage of the logo slot */
  get logoStyle() {
    return { maxWidth: this.localScale + '%', height: 'auto' };
  }

  /** Logo max-width in expanded dialog */
  get expandedLogoStyle() {
    return { maxWidth: this.localScale + '%', height: 'auto' };
  }

  onScaleChange(value: number) {
    this.$emit("update:scaleFactor", value);
  }

  onDragOver() {
    if (!this.readonly) {
      this.isDragging = true;
    }
  }

  onDrop(event: DragEvent) {
    this.isDragging = false;
    if (this.readonly) return;
    const file = event.dataTransfer?.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      this.trimAndEmit(file);
    }
  }

  onUpload() {
    const file = (this.uploadElement.files ?? [])[0] ?? null;
    if (file) {
      this.trimAndEmit(file);
    }
  }

  /** Trim fully transparent padding from image before emitting */
  async trimAndEmit(file: File) {
    // Only PNG can have transparency
    if (file.type !== "image/png") {
      this.$emit("upload", file);
      return;
    }
    try {
      const trimmed = await this.trimTransparentEdges(file);
      this.$emit("upload", trimmed);
    } catch {
      this.$emit("upload", file);
    }
  }

  /** Crops fully transparent (alpha=0) edges from a PNG */
  private trimTransparentEdges(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("no ctx")); return; }

        ctx.drawImage(img, 0, 0);
        const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);

        let top = height, left = width, right = 0, bottom = 0;

        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const a = data[(y * width + x) * 4 + 3];
            if (a === 0) continue; // fully transparent — skip
            if (y < top) top = y;
            if (y > bottom) bottom = y;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }

        // No visible content or no trimming needed
        if (top >= bottom || left >= right) { resolve(file); return; }
        if (top === 0 && left === 0 && right === width - 1 && bottom === height - 1) {
          resolve(file); return;
        }

        const cropW = right - left + 1;
        const cropH = bottom - top + 1;
        const cropCanvas = document.createElement("canvas");
        cropCanvas.width = cropW;
        cropCanvas.height = cropH;
        const cropCtx = cropCanvas.getContext("2d");
        if (!cropCtx) { reject(new Error("no ctx")); return; }

        cropCtx.drawImage(img, left, top, cropW, cropH, 0, 0, cropW, cropH);
        cropCanvas.toBlob((blob) => {
          if (!blob) { reject(new Error("toBlob failed")); return; }
          resolve(new File([blob], file.name, { type: file.type }));
        }, file.type);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("load failed")); };
      img.src = url;
    });
  }

  onPreviewClick() {
    if (this.readonly || this.compactMode) return;
    this.showExpandDialog = true;
  }

  onLogoSlotClick() {
    if (this.compactMode && !this.readonly) {
      this.showExpandDialog = true;
      return;
    }
  }

  onRemoveLogo() {
    this.$emit("remove");
  }

  onRemoveLogoAndClose() {
    this.$emit("remove");
    this.showExpandDialog = false;
  }
}
